<script setup lang="ts">
import { useFormat } from '@/composables/format';
import type { CatalogGenderGroup, CatalogProductPrice } from '@onrunning/backstage-models';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ProductCard from './ProductCard.vue';

const GENDER_ORDER = ['Unisex', 'Women', 'Men'];
// One row in the colorway grid fits 6 cards. A gender that needs more than one row
// gets its own dedicated page; genders that fit in a single row share a page.
const COLORWAYS_PER_ROW = 6;
// Maximum colorways shown per page for a multi-row gender (6 cols × 3 rows).
const MAX_COLORWAYS_PER_PAGE = 18;
// Maximum number of single-row genders that can share one page (6 cols × 3 rows = 3 rows max).
const MAX_SINGLE_ROW_GENDERS_PER_PAGE = 3;

const props = defineProps<{
  productName: string;
  pricing?: CatalogProductPrice;
  genders: CatalogGenderGroup[];
}>();

const { t } = useI18n();

const { parseUtcDateToLocaleDate } = useFormat();

// Sort genders: Unisex → Women → Men, unknowns last
const sortedGenders = computed(() =>
  [...props.genders].sort((a, b) => {
    const ia = GENDER_ORDER.indexOf(a.genderName);
    const ib = GENDER_ORDER.indexOf(b.genderName);

    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  }),
);

// Genders with more than one row of colorways (> COLORWAYS_PER_ROW) get their own
// dedicated page. Single-row genders (≤ COLORWAYS_PER_ROW) are grouped together.
// A gender with more than MAX_COLORWAYS_PER_PAGE colorways is split across multiple pages.
const pages = computed<CatalogGenderGroup[][]>(() => {
  const result: CatalogGenderGroup[][] = [];
  let currentPage: CatalogGenderGroup[] = [];

  for (const gender of sortedGenders.value) {
    if (gender.colorways.length <= COLORWAYS_PER_ROW) {
      // Single-row: share the page with other single-row genders, up to the row cap
      if (currentPage.length >= MAX_SINGLE_ROW_GENDERS_PER_PAGE) {
        result.push(currentPage);
        currentPage = [];
      }
      currentPage.push(gender);
    } else {
      // Multi-row: flush current single-row page first, then chunk this gender
      if (currentPage.length) {
        result.push(currentPage);
        currentPage = [];
      }
      for (let i = 0; i < gender.colorways.length; i += MAX_COLORWAYS_PER_PAGE) {
        result.push([{ ...gender, colorways: gender.colorways.slice(i, i + MAX_COLORWAYS_PER_PAGE) }]);
      }
    }
  }

  if (currentPage.length) result.push(currentPage);

  return result.length ? result : [[]];
});
</script>

<template>
  <div v-for="(pageGenders, pageIndex) in pages" :key="pageIndex" class="product-page">
    <!-- Header: logo + product name + optional pricing -->
    <div class="product-header">
      <div class="header-left">
        <img src="@/assets/img/black-logo.svg" alt="On" class="page-logo" />
        <span class="product-name">{{ productName }}</span>
      </div>
      <div v-if="pricing" class="header-pricing">
        <p>
          <strong>{{ t('seasonCatalogPdf.prices.wsp') }}:</strong> {{ pricing.currency }}
          {{ Number(pricing.wholesalePrice).toFixed(2) }}
        </p>
        <p>
          <strong>{{ t('seasonCatalogPdf.prices.rrp') }}:</strong> {{ pricing.currency }}
          {{ Number(pricing.retailerPrice).toFixed(2) }}
        </p>
      </div>
    </div>

    <!-- Gender sections on this page -->
    <div v-for="gender in pageGenders" :key="gender.genderName" class="gender-section">
      <!-- Variant overview: gender name + specs -->
      <div class="variant-overview">
        <span class="gender-name">{{ gender.genderName }}</span>
        <span class="spec">
          <img src="@/assets/img/icons/size_icon.svg" :alt="t('seasonCatalogPdf.specs.size')" />
          {{ gender.specs.sizeRange }}
        </span>
        <span class="spec">
          <img src="@/assets/img/icons/weight_icon.svg" :alt="t('seasonCatalogPdf.specs.weight')" />
          {{ gender.specs.weightGrams }} g
        </span>
        <span v-if="gender.specs.dropMM" class="spec">
          <img
            src="@/assets/img/icons/heel_to_toe_offset_icon.svg"
            :alt="t('seasonCatalogPdf.specs.heel_to_toe_offset')" />
          {{ gender.specs.dropMM }}
        </span>
      </div>

      <!-- Colorway grid: 6 per row -->
      <div class="colorway-grid">
        <ProductCard
          v-for="colorway in gender.colorways"
          :key="colorway.itemNumber"
          :labels="colorway.labels"
          :image-source="colorway.imageUrl || undefined">
          <p class="colorway-grid__text">{{ colorway.colorName }}</p>
          <p class="colorway-grid__text">{{ colorway.itemNumber }}</p>
          <p class="colorway-grid__text">{{ parseUtcDateToLocaleDate(colorway.launchDate) }}</p>
        </ProductCard>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-page {
  width: 297mm;
  height: 210mm;
  background-color: var(--pdf-background-light-color);
  page-break-after: always;
  padding: 1rem 2rem;
  box-sizing: border-box;
  overflow: hidden;

  .product-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 42px;
    margin-bottom: 0;

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;

      .page-logo {
        width: 16px;
        flex-shrink: 0;
      }

      .product-name {
        font-size: 1.125rem;
        color: var(--primary-dark);
      }
    }

    .header-pricing {
      text-align: right;

      p {
        font-size: 0.75rem;
        color: var(--primary-dark);
        margin: 0;
        line-height: 1.4;
      }
    }
  }

  .gender-section {
    margin-top: 16px;

    .variant-overview {
      display: flex;
      align-items: center;
      gap: 20px;
      margin-bottom: 8px;

      .gender-name {
        font-size: 0.75rem;
        color: var(--primary-dark);
      }

      .spec {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 0.5rem;
        color: var(--primary-dark);

        img {
          width: 20px;
          height: 20px;
        }
      }
    }

    .colorway-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, 160px);
      gap: 10px;

      &__text {
        margin: 5px 0 0 0;
      }
    }
  }
}
</style>
